import * as React from 'react';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import Moment from 'moment';
import propz from 'propz';
import { AGE_GROUPS } from 'Src/consts/school';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, LIMIT } from 'Src/consts/table';
import { TEAM_TYPE } from 'Src/consts/team';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../../../../consts/common';
import { Loader } from 'Src/components/Loader/Loader';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { Switch } from 'Src/components/Switch/Switch';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { Button } from 'Src/components/Button/Button';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Select } from 'Src/components/Select/Select';
import { getAge, getGender } from 'Src/helpers/accessor/accessor';
import { getTwoPanelEditorFilters } from 'Src/helpers/twoPanelEditor/twoPanelEditor';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import {
  ColumnDefinition,
  getOrder,
  getServerFieldSectionWhere,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses,
  getSelectOptionForTeams
} from 'Src/helpers/table/select';
import {
  getUserGenderArrayConvertedFromEventGender,
  isIndividualTournament,
  isTeamTournament
} from 'Src/helpers/tournament/tournament';
import { getSchoolEvent, updateSchoolEventTeamPlayersBatch } from 'Src/helpers/service/admin/event';
import {
  getEventClash,
  getEventTitle,
  getSchoolEventAges,
  getStudentsMethod,
  getTeamPlayersMethod
} from 'Src/helpers/event/event';
import {
  addSchoolEventTeam,
  addSchoolTournamentTeam,
  getSchoolTournamentTeams
} from 'Src/helpers/service/admin/tournamentTeams';
import { getSchoolEventTeam, updateSchoolEventTeam } from 'Src/helpers/service/admin/schoolEventTeams';
import {
  getSchoolStudentsWithClashesCount,
  getSchoolStudentsWithoutClashes
} from 'Src/helpers/service/admin/schoolTeamPlayers';
import { getSchoolEventTeamPlayersCount } from 'Src/helpers/service/admin/schoolEventTeamPlayers';
import { getEventStartDateAndTime } from '../../../../../helpers/event/schoolEventViewHelper';
import {
  getSchoolTournamentTeamPlayers,
  updateSchoolTournamentTeamPlayersBatch
} from 'Src/helpers/service/admin/tournamentTeamPlayers';
import { getColorClashes } from 'Src/helpers/cell/cell';
import { SchoolStudentClash } from 'Src/models/schoolStudent';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { SchoolEvent } from 'Src/models/event';
import { formatAgesCompactForSchool, formatTeamAgesCompact, resolveAgeNamingFromSchool } from 'Src/helpers/ageGroups';
import { SchoolEventTeamPlayer, SchoolEventTeamPlayerWithClashes } from 'Src/models/eventTeamPlayer';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { SlotAssignment, SlotAssignmentModal } from '../SlotAssignmentModal';
import { getTournamentEventParticipationSummary } from 'Src/helpers/service/adminSU/tournamentEventParticipants';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  onCancelClick: (itemsAddBuffer, itemsRemoveBuffer) => void;
  onSaveClick: () => void;
}

interface State {
  items: Item[];
  itemsSelected: Item[];
  itemsCount: number;

  itemsFilters: any;
  itemsSearch: string;
  isItemsLoading: boolean;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;

  isSelectAllItemsChecked: boolean;

  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;

  itemCurrentPage: number;

  itemsAccumulatorInitial: Item[];
  itemsAccumulator: Item[];
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorCount: number;

  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;

  isSelectAllItemsAccumulatorChecked: boolean;

  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  isLoading: boolean;
  isError: boolean;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  selectedTeamId: string;
  team: any;
  tournamentTeams: TournamentTeam[];

  forms: SchoolForm[];
  houses: SchoolHouse[];

  studentEventsModalText: string;
  isStudentEventsModalOpen: boolean;

  isShowYounger: boolean;
  isMaxPlayersModalOpen: boolean;
  maxPlayersModalMessage: string;
  isHelpModalOpen: boolean;
  isExitConfirmationModalOpen: boolean;
  clashEventsById: Record<string, SchoolEvent>;
  slotCandidates: Item[];
}

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: {
    status?: string;
    fullClashEvents?: any[];
    intervalClashEvents?: any[];
  };
  playerId?: string;
  eligibilityWarning?: string;
  tournamentEvents?: string;
}

const getEligibleStudentNameCell = (item: Item): React.ReactNode => (
  <div className="tournamentEventParticipants__studentName">
    <span>{item.firstName}</span>
    {item.tournamentEvents && <small title={item.tournamentEvents}>Also in: {item.tournamentEvents}</small>}
  </div>
);

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender
  }
];

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender
  }
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    cell: getEligibleStudentNameCell,
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    cell: getEligibleStudentNameCell,
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  }
];

const DEFAULT_TEAM_PLAYERS_ORDER = 'firstName ASC';

export class TournamentSchoolTeamManager extends React.Component<Props, State> {
  private itemsSearchRequestId = 0;
  private clashEventIdsLoading = new Set<string>();
  private tournamentEventsByStudentId: Record<string, string> = {};
  private tournamentStudentIds: string[] = [];

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      itemsCount: 0,
      itemsSelected: [],

      itemsFilters: {},
      itemsSearch: '',
      isItemsLoading: false,
      isShowItemsFilter: false,
      isItemsFiltered: false,

      isSelectAllItemsChecked: false,

      sortItemsDirection: '',
      sortItemsColumnsName: '',

      itemCurrentPage: 1,

      itemsAccumulatorInitial: [],
      itemsAccumulator: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],

      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,

      isSelectAllItemsAccumulatorChecked: false,

      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      isLoading: true,
      isError: false,

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      selectedTeamId: this.getTeamId(),
      tournamentTeams: [],
      team: undefined,
      forms: [],
      houses: [],

      studentEventsModalText: '',
      isStudentEventsModalOpen: false,

      isShowYounger: false,
      isMaxPlayersModalOpen: false,
      maxPlayersModalMessage: '',
      isHelpModalOpen: false,
      isExitConfirmationModalOpen: false,
      clashEventsById: {},
      slotCandidates: []
    };
  }

  getDefaultItemsFilter(team, event: SchoolEvent, forms: SchoolForm[] = this.state.forms) {
    const isTeamExist = typeof team !== 'undefined';
    if (isTeamExist) {
      const { gender, ages } = team;

      const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);

      return {
        firstName: '',
        lastName: '',
        gender: genderArrayConverted,
        forms: [],
        ages: ages
      };
    } else {
      const { gender, ages } = event;
      const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);

      return {
        firstName: '',
        lastName: '',
        gender: genderArrayConverted,
        forms: [],
        ages: ages
      };
    }
  }

  getTeamId(): string {
    const { event, user } = this.props;
    const { activeSchoolId } = user;
    const { teamsData } = event;
    const team = teamsData.find(teamData => teamData.schoolId === activeSchoolId);

    return typeof team !== 'undefined' ? team.id : undefined;
  }

  getEventId(): string {
    const { event } = this.props;
    const { id } = event;
    return id;
  }

  componentDidMount() {
    this.setItems();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedTeamId !== this.state.selectedTeamId) {
      this.setItems();
    }
    if (prevState.items !== this.state.items || prevState.itemsAccumulator !== this.state.itemsAccumulator) {
      this.loadClashEventNames([...this.state.items, ...this.state.itemsAccumulator]);
    }
  }

  loadClashEventNames = (students: Item[]) => {
    const { user } = this.props;
    const missingIds = Array.from(
      new Set(
        students.reduce<string[]>((ids, student) => {
          const events = [...(student.clashes.fullClashEvents || []), ...(student.clashes.intervalClashEvents || [])];
          events.forEach((event: any) => {
            const id = String(event._id || event.id || '');
            if (id && !this.state.clashEventsById[id] && !this.clashEventIdsLoading.has(id)) ids.push(id);
          });
          return ids;
        }, [])
      )
    );
    if (!missingIds.length) return;
    missingIds.forEach(id => this.clashEventIdsLoading.add(id));
    Promise.all(missingIds.map(id => getSchoolEvent(user, id).then(event => ({ id, event }))))
      .then(entries => {
        entries.forEach(({ id }) => this.clashEventIdsLoading.delete(id));
        this.setState(state => ({
          clashEventsById: entries.reduce(
            (result, { id, event }) => ({ ...result, [id]: event }),
            state.clashEventsById
          )
        }));
      })
      .catch(() => missingIds.forEach(id => this.clashEventIdsLoading.delete(id)));
  };

  setItems() {
    const { selectedTeamId } = this.state;

    this.setState({
      isLoading: true
    });

    const { user, event } = this.props;
    const eventId = this.getEventId();

    const tournamentId = propz.get(event, ['tournament', 'id']);

    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);
    const isItemsFiltered = isFilterExist2(itemsFilters);

    const itemsAccumulatorFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);
    const isShowItemsAccumulatorFilter = isFilterExist2(itemsAccumulatorFilters);
    const isItemsAccumulatorFiltered = isFilterExist2(itemsAccumulatorFilters);

    const isSelectedTeamIdExist = typeof selectedTeamId !== 'undefined';

    const teamPromise = isSelectedTeamIdExist
      ? getSchoolEventTeam(user, eventId, selectedTeamId)
      : Promise.resolve(undefined);

    const tournamentTeamsPromise = getSchoolTournamentTeams(user, tournamentId);
    const formsPromise = getAllForms(user);
    const housesPromise = getAllHouses(user);

    Promise.all([teamPromise, tournamentTeamsPromise, formsPromise, housesPromise])
      .then(([team, tournamentTeams, forms, houses]) => {
        const isIndividualTournamentEvent = isIndividualTournament(event.tournament);
        const tournamentPlayersPromise = isIndividualTournamentEvent
          ? Promise.all(
              (tournamentTeams || []).map(tournamentTeam =>
                getSchoolTournamentTeamPlayers(
                  user,
                  tournamentId,
                  tournamentTeam.id,
                  { limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP },
                  false
                )
              )
            ).then(teamPlayers => teamPlayers.reduce((allPlayers, players) => allPlayers.concat(players || []), []))
          : Promise.resolve([]);

        return tournamentPlayersPromise.then(tournamentPlayers => {
          const tournamentStudentIds = Array.from(
            new Set((tournamentPlayers || []).map(player => String(player.userId)).filter(Boolean))
          );
          const participationSummaryPromise = isIndividualTournamentEvent
            ? getTournamentEventParticipationSummary(user, tournamentId, tournamentStudentIds).catch(() => [])
            : Promise.resolve([]);
          return participationSummaryPromise.then(participationSummary => ({
            team,
            tournamentTeams,
            forms,
            houses,
            tournamentPlayers,
            participationSummary,
            isIndividualTournamentEvent
          }));
        });
      })
      .then(
        ({
          team,
          tournamentTeams,
          forms,
          houses,
          tournamentPlayers,
          participationSummary,
          isIndividualTournamentEvent
        }) => {
          if (isIndividualTournamentEvent) {
            this.tournamentStudentIds = Array.from(
              new Set((tournamentPlayers || []).map(player => String(player.userId)).filter(Boolean))
            );
            const groupedEvents = participationSummary.reduce((eventsByStudentId, participation) => {
              const studentId = String(participation.userId);
              const eventName = [participation.sportName, participation.shortDescription]
                .map(value => String(value || '').trim())
                .filter(Boolean)
                .join(' · ');
              const existing = eventsByStudentId[studentId] || [];
              if (eventName && !existing.includes(eventName)) existing.push(eventName);
              eventsByStudentId[studentId] = existing;
              return eventsByStudentId;
            }, {} as Record<string, string[]>);
            const eventsByUserId = Object.keys(groupedEvents).reduce(
              (result, studentId) => ({ ...result, [studentId]: groupedEvents[studentId].join(', ') }),
              {}
            );
            this.tournamentEventsByStudentId = (tournamentPlayers || []).reduce((result, player) => {
              const events = eventsByUserId[String(player.userId)];
              if (!events) return result;
              result[String(player.userId)] = events;
              if (player.permissionId) result[String(player.permissionId)] = events;
              return result;
            }, { ...eventsByUserId } as Record<string, string>);
          } else {
            this.tournamentStudentIds = [];
            this.tournamentEventsByStudentId = {};
          }

          const defaultItemsFilter = this.getDefaultItemsFilter(team, event, forms);
          const isTeamExist = typeof team !== 'undefined';

          isTeamExist
            ? this.setState({
                team: team,
                tournamentTeams: tournamentTeams,
                forms: forms,
                houses: houses,
                itemsFilters: defaultItemsFilter
              })
            : this.setState({
                forms: forms,
                houses: houses,
                tournamentTeams: tournamentTeams,
                itemsFilters: defaultItemsFilter
              });

          return this.getItemsAndItemsAccumulator();
        }
      )
      .then(({ teamPlayersInitial, teamPlayers, teamPlayersCount, students, studentsCount }) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);
        const teamPlayersInitialNormalized = this.getTeamPlayersNormalized(teamPlayersInitial);

        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          isShowItemsFilter: isShowItemsFilter,
          isItemsFiltered: isItemsFiltered,

          itemsAccumulatorInitial: teamPlayersInitialNormalized,
          itemsAccumulator: teamPlayersNormalized,
          itemsAccumulatorCount: teamPlayersCount.count,
          itemsAccumulatorFilters: itemsAccumulatorFilters,
          isShowItemsAccumulatorFilter: isShowItemsAccumulatorFilter,
          isItemsAccumulatorFiltered: isItemsAccumulatorFiltered,

          isLoading: false
        });
      });
  }

  getTeamPlayersNormalized(teamPlayers: SchoolEventTeamPlayerWithClashes[]): Item[] {
    return teamPlayers.map(teamPlayer => {
      const { permissionId, form, gender, firstName, lastName, userId, id, house, clashes } = teamPlayer;
      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes: clashes,
        id: userId,
        playerId: id,
        tournamentEvents:
          this.tournamentEventsByStudentId[String(userId)] ||
          this.tournamentEventsByStudentId[String(permissionId)] ||
          '',
        eligibilityWarning: this.getEligibilityWarning(form)
      };
    });
  }

  getStudentsNormalized(students: SchoolStudentClash[]): Item[] {
    return students.map(student => {
      const { permissionId, form, gender, firstName, lastName, id, house, clashes } = student;

      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes,
        id,
        tournamentEvents:
          this.tournamentEventsByStudentId[String(id)] ||
          this.tournamentEventsByStudentId[String(permissionId)] ||
          '',
        eligibilityWarning: this.getEligibilityWarning(form)
      };
    });
  }

  getEligibilityWarning(form: any): string {
    const { event, user } = this.props;
    const studentAge = Number(propz.get(form, ['age']));
    if (!Number.isFinite(studentAge) || event.ages.includes(studentAge)) return '';
    return `Age: event is ${formatAgesCompactForSchool(
      event.ages,
      user.activeSchool
    )}, student is ${formatAgesCompactForSchool([studentAge], user.activeSchool)}`;
  }

  getItemsFilter(page, itemsAccumulator, itemsAddBuffer, isAllItems = false) {
    const { itemsFilters, forms, selectedTeamId, itemsSearch } = this.state;
    const { event } = this.props;

    const { startTime, endTime, id } = event;
    const { players } = event;

    const {
      gender: itemsFiltersGender,
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = '',
      forms: itemsFiltersForms = [],
      houses: itemsFiltersHouses = []
    } = itemsFilters;

    let where = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const playerUserIdsFromOtherTeams = players
      .filter(player => player.teamId !== selectedTeamId)
      .map(player => ({
        id: player.userId
      }));

    const allItems = [...itemsAccumulator, ...itemsAddBuffer, ...playerUserIdsFromOtherTeams];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);
    if (isIndividualTournament(event.tournament)) {
      propz.set(where, ['_id', '$in'], this.tournamentStudentIds);
    }

    if (itemsFiltersForms.length > 0) {
      propz.set(where, ['formId', '$in'], itemsFiltersForms);
    } else {
      const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
      const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

      if (formIds.length > 0) {
        propz.set(where, ['formId', '$in'], formIds);
      }
    }

    if (itemsFiltersHouses.length > 0) {
      propz.set(where, ['houseId', '$in'], itemsFiltersHouses);
    }

    let and: any[] = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    // copy-paste from old frontend
    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    if (!startTime || !endTime) return;

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order: DEFAULT_TEAM_PLAYERS_ORDER
    };

    return filter;
  }

  getItemsAndItemsAccumulator() {
    const { user } = this.props;
    const { itemsAccumulatorFilters, forms, selectedTeamId } = this.state;

    const options = { forms };
    const where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);

    const eventId = this.getEventId();
    const isSelectedTeamIdExist = typeof selectedTeamId !== 'undefined';

    const getTeamPlayersInitialPromise: Promise<SchoolEventTeamPlayerWithClashes[]> = isSelectedTeamIdExist
      ? getTeamPlayersMethod(user)(user, eventId, selectedTeamId, {})
      : Promise.resolve([]);
    const getTeamPlayersPromise: Promise<SchoolEventTeamPlayer[]> = isSelectedTeamIdExist
      ? getTeamPlayersMethod(user)(user, eventId, selectedTeamId, {})
      : Promise.resolve([]);
    const getTeamPlayersCountPromise = isSelectedTeamIdExist
      ? getSchoolEventTeamPlayersCount(user, eventId, selectedTeamId, where)
      : Promise.resolve({ count: 0 });

    let teamPlayersInitial, teamPlayers, teamPlayersCount;

    return Promise.all([getTeamPlayersInitialPromise, getTeamPlayersPromise, getTeamPlayersCountPromise])
      .then(([_teamPlayersInitial, _teamPlayers, _teamPlayersCount]) => {
        teamPlayersInitial = _teamPlayersInitial;
        teamPlayers = _teamPlayers;
        teamPlayersCount = _teamPlayersCount;

        const teamPlayersInitialNormalized = this.getTeamPlayersNormalized(teamPlayersInitial);

        const filter = this.getItemsFilter(1, teamPlayersInitialNormalized, []);

        const method = getStudentsMethod(user);
        const getStudentsPromise = method(user, filter);
        const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

        return Promise.all([getStudentsPromise, getStudentsCountPromise]);
      })
      .then(([students, studentsCount]) => {
        return {
          teamPlayersInitial,
          teamPlayers,
          teamPlayersCount,
          students,
          studentsCount
        };
      });
  }

  onItemClick = (index: number): void => {
    const { items, itemsSelected } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = itemsSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: [],
      itemsSelected: selectedItemsUpdated
    });
  };

  onItemAccumulatorClick = (index: number): void => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const selectedItem = itemsAccumulator[index];

    const selectedItemIndex = itemsAccumulatorSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsAccumulatorSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: selectedItemsUpdated,
      itemsSelected: []
    });
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const { user } = this.props;

    const selectedIds = new Set((itemsAccumulatorSelected || []).map(x => String(x.id)));
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));

    const selectedExistingItems = (itemsAccumulatorSelected || []).filter(x => !addBufferIds.has(String(x.id)));

    const nextItemsAddBuffer = (itemsAddBuffer || []).filter(x => !selectedIds.has(String(x.id)));

    const existingRemoveIds = new Set((itemsRemoveBuffer || []).map(x => String(x.id)));
    const nextItemsRemoveBuffer = [
      ...(itemsRemoveBuffer || []),
      ...selectedExistingItems.filter(x => !existingRemoveIds.has(String(x.id)))
    ];

    const nextItemsAccumulatorInitial = (itemsAccumulatorInitial || []).filter(
      accItem => !nextItemsRemoveBuffer.some(rem => String(rem.id) === String(accItem.id))
    );

    const nextItemsAccumulator = (itemsAccumulator || []).filter(accItem => !selectedIds.has(String(accItem.id)));

    const filter = this.getItemsFilter(itemCurrentPage, nextItemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsRemoveBuffer: nextItemsRemoveBuffer,
        itemsAccumulatorInitial: nextItemsAccumulatorInitial,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length
      });
    });
  };

  getTeam() {
    const { user, event } = this.props;
    const { activeSchoolId } = user;
    const { ages, gender, sportId } = event;
    const sportName = propz.get(event, ['sport', 'name'], '');
    const schoolName = propz.get(user, ['activeSchoolName'], '');
    const name = `${schoolName} ${sportName}`.trim() || 'Team';

    return {
      ages: ages.map(age => Number(age)),
      gender: gender,
      schoolId: activeSchoolId,
      sportId: sportId,
      teamType: TEAM_TYPE.ADHOC,
      name: name
    };
  }

  onClickSave = () => {
    const { itemsAddBuffer, itemsRemoveBuffer, selectedTeamId, tournamentTeams } = this.state;
    const { user, event, onSaveClick } = this.props;
    const { activeSchoolId } = user;
    const { tournamentId } = event as any;
    const eventId = this.getEventId();

    this.setState({
      isLoading: true
    });

    const currentSchoolTournamentTeam = tournamentTeams.find(item => {
      return item.schoolId === activeSchoolId;
    });

    const isCurrentSchoolTeamExist = typeof currentSchoolTournamentTeam !== 'undefined';

    const tournamentTeamsPlayers = propz.get(currentSchoolTournamentTeam, ['players'], []);
    const tournamentTeamsPlayersIds = tournamentTeamsPlayers.map(player => player.userId);

    const studentsToAddToTeam = itemsAddBuffer.filter(item => !tournamentTeamsPlayersIds.includes(item.id));
    const isStudentsToAddToTeamExist = studentsToAddToTeam.length > 0;

    const data = {
      add: itemsAddBuffer.map(item => ({ userId: item.id, permissionId: item.permissionId })),
      remove: itemsRemoveBuffer.map(item => item.playerId).filter((id): id is string => typeof id === 'string')
    };

    const isSelectedTeamExist = typeof selectedTeamId !== 'undefined';
    const team = this.getTeam();
    const teamPromise = isSelectedTeamExist ? Promise.resolve(undefined) : addSchoolEventTeam(user, eventId, team);

    teamPromise
      .then(addTeam => {
        return isSelectedTeamExist
          ? updateSchoolEventTeamPlayersBatch(user, eventId, selectedTeamId, data)
          : updateSchoolEventTeamPlayersBatch(user, eventId, addTeam.id, data);
      })
      .then(() => {
        switch (true) {
          case isCurrentSchoolTeamExist:
            if (isStudentsToAddToTeamExist) {
              const addToTeamData = {
                add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
                remove: []
              };

              return updateSchoolTournamentTeamPlayersBatch(
                user,
                tournamentId,
                currentSchoolTournamentTeam.id,
                addToTeamData
              );
            }

            break;

          case !isCurrentSchoolTeamExist:
            return this.createDefaultTournamentTeam();
        }
      })
      .then(newTeam => {
        if (!isCurrentSchoolTeamExist) {
          const addToTeamData = {
            add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
            remove: []
          };

          return updateSchoolTournamentTeamPlayersBatch(user, tournamentId, newTeam.id, addToTeamData);
        }
      })
      .then(() => {
        onSaveClick();
      });
  };

  createDefaultTournamentTeam = () => {
    const { user, event } = this.props;
    const { activeSchoolId: schoolId } = user;
    const { tournament, tournamentId } = event as any;
    const { gender, ages, sportId } = tournament;
    const tournamentName = propz.get(tournament, ['name'], '');
    const schoolName = propz.get(user, ['activeSchoolName'], '');
    const name = `${schoolName} ${tournamentName}`;

    const teamData = {
      name,
      ages,
      gender,
      sportId,
      schoolId,
      teamType: TEAM_TYPE.ADHOC,
      isAutoEnrolledTeam: true,
      players: []
    };

    return addSchoolTournamentTeam(user, tournamentId, teamData);
  };

  onItemsFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (filterField === 'forms') {
      nextFilters.ages = [];
    }

    if (filterField === 'ages') {
      nextFilters.forms = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsFilters: nextFilters
    });
  };

  onItemsAccumulatorFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsAccumulatorFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsAccumulatorFilters: nextFilters
    });
  };

  onItemsFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsFilter } = this.state;

    this.setState({
      isShowItemsFilter: !isShowItemsFilter
    });
  };

  onItemsAccumulatorFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsAccumulatorFilter } = this.state;

    this.setState({
      isShowItemsAccumulatorFilter: !isShowItemsAccumulatorFilter
    });
  };

  itemsSetCurrentPageParams = (currentPage: number): void => {
    const { user } = this.props;
    const { itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(currentPage, itemsAccumulator, itemsAddBuffer);

    const method = getStudentsMethod(user);

    method(user, filter).then(students => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        itemCurrentPage: currentPage,
        items: studentsNormalized,
        isLoading: false
      });
    });
  };

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulator, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;

    if (isSilent) {
      this.setState({ isItemsLoading: true });
    } else {
      this.setState({ isLoading: true });
    }

    const filter = this.getItemsFilter(1, itemsAccumulator, itemsAddBuffer);

    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentCountPromise]).then(([students, studentsCount]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        isItemsLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsDirection: '',
        sortItemsColumnsName: ''
      });
    });
  };

  onItemsSearchChange = (itemsSearch: string) => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  itemsAccumulatorOnApplyFilterClick = () => {};
  // itemsAccumulatorOnApplyFilterClick = () => {
  //   const { itemsAccumulatorFilters, itemsRemoveBuffer, forms } = this.state;
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const options = { forms };
  //
  //   let where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);
  //   const ninIds = itemsRemoveBuffer.map(item => item.id);
  //   propz.set(where, ['id', '$nin'], ninIds);
  //
  //   const { user } = this.props;
  //
  //   const teamId = this.getTeamId();
  //   const eventId = this.getEventId();
  //
  //   const getTeamPlayersPromise = getAllSchoolEventTeamPlayers(user, eventId, teamId, where);
  //   const getTeamPlayersCountPromise = getSchoolEventTeamPlayersCount(user, eventId, teamId, where);
  //
  //   BPromise.all([getTeamPlayersPromise, getTeamPlayersCountPromise]).then(([teamPlayers, teamPlayersCount]) => {
  //     const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);
  //
  //     this.setState({
  //       itemsAccumulator: teamPlayersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: teamPlayersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: []
  //     });
  //   });
  // };

  itemsOnClearFilterClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer, team, forms, selectedTeamId } = this.state;
    const { event } = this.props;

    const defaultItemsFilter = this.getDefaultItemsFilter(team, event);

    this.setState({
      isLoading: true,
      itemsFilters: defaultItemsFilter
    });

    const { players, id } = event;
    const playerUserIdsFromOtherTeams = players
      .filter(player => player.teamId !== selectedTeamId)
      .map(player => ({
        id: player.userId
      }));

    const { gender: itemsFiltersGender, ages: itemsFiltersAges = [] } = defaultItemsFilter;

    let where = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const allItems = [...itemsAccumulatorInitial, ...itemsAddBuffer, ...playerUserIdsFromOtherTeams];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
    const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

    if (formIds.length > 0) {
      propz.set(where, ['formId', '$in'], formIds);
    }

    const { startTime, endTime } = event;

    // copy-paste from old frontend
    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    if (!startTime || !endTime) return;

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: LIMIT,
      skip: DEFAULT_SKIP,
      order: DEFAULT_TEAM_PLAYERS_ORDER
    };

    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: '',
        sortItemsDirection: ''
      });
    });
  };

  itemsAccumulatorOnClearFilterClick = () => {};
  // itemsAccumulatorOnClearFilterClick = () => {
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const { user } = this.props;
  //   const teamId = this.getTeamId();
  //   const { itemsRemoveBuffer } = this.state;
  //
  //   const eventId = this.getEventId();
  //
  //   const getTeamPlayersPromise = getAllSchoolEventTeamPlayers(user, eventId, teamId);
  //   const getTeamPlayersCountPromise = getSchoolEventTeamPlayersCount(user, eventId, teamId);
  //
  //   BPromise.all([getTeamPlayersPromise, getTeamPlayersCountPromise]).then(([teamPlayers, teamPlayersCount]) => {
  //     const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);
  //     const nextTeamPlayersNormalized = teamPlayersNormalized.filter(teamPlayerNormalized =>
  //       itemsRemoveBuffer.every(item => item.id !== teamPlayerNormalized.id)
  //     );
  //
  //     this.setState({
  //       itemsAccumulator: nextTeamPlayersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: teamPlayersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: [],
  //       itemsAccumulatorFilters: {}
  //     });
  //   });
  // };

  onSelectAllItemsClick = () => {
    const { itemsSelected, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer, true);

    getSchoolStudentsWithoutClashes(user, filter).then(items => {
      const itemsFiltered = items.filter(item => {
        return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
      });

      const studentsNormalizedFiltered = this.getStudentsNormalized(itemsFiltered);

      const nextSelectedItems = [...itemsSelected, ...studentsNormalizedFiltered];

      this.setState({
        itemsSelected: nextSelectedItems,
        isSelectAllItemsChecked: true,
        itemsAccumulatorSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsOnPageClick = () => {
    const { items, itemsSelected } = this.state;

    const itemsFiltered = items.filter(item => {
      return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsSelected, ...itemsFiltered];

    this.setState({
      itemsSelected: nextSelectedItems,
      isSelectAllItemsChecked: true,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onUnselectAllItemsClick = () => {
    this.setState({
      itemsSelected: [],
      isSelectAllItemsChecked: false,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onSelectAllItemsAccumulatorClick = () => {
    const { itemsAccumulatorSelected, itemsRemoveBuffer, selectedTeamId } = this.state;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const eventId = this.getEventId();

    getTeamPlayersMethod(user)(user, eventId, selectedTeamId, {}).then(teamPlayers => {
      const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);

      const teamPlayersFiltered = teamPlayersNormalized.filter(teamPlayer => {
        return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== teamPlayer.id);
      });

      const nextSelectedItems = [...itemsAccumulatorSelected, ...teamPlayersFiltered].filter(item => {
        return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
      });

      this.setState({
        itemsAccumulatorSelected: nextSelectedItems,
        isSelectAllItemsAccumulatorChecked: true,
        itemsSelected: [],
        isSelectAllItemsChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsAccumulatorOnPageClick = () => {
    const { itemsAccumulator, itemsAccumulatorSelected, itemsRemoveBuffer } = this.state;

    const itemsFiltered = itemsAccumulator.filter(item => {
      return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsAccumulatorSelected, ...itemsFiltered].filter(item => {
      return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
    });

    this.setState({
      itemsAccumulatorSelected: nextSelectedItems,
      isSelectAllItemsAccumulatorChecked: true,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onUnselectAllItemsAccumulatorClick = () => {
    this.setState({
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onSortItemsClick = (sortField: string) => {
    const { sortItemsDirection, sortItemsColumnsName, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    const order = getOrder(sortField, sortItemsDirection, sortItemsColumnsName) as TABLE_SORT_DIRECTION;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer);
    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: sortField,
        sortItemsDirection: order
      });
    });
  };

  onSortItemsAccumulatorClick = (sortField: string) => {};

  onClashClick = (student: SchoolStudentClash | SchoolEventTeamPlayerWithClashes) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = student;
    const { fullClashEvents, intervalClashEvents } = clashes;
    const fullClashEventIds = fullClashEvents.map(fullClashEvent => fullClashEvent._id);
    const intervalClashEventIds = intervalClashEvents.map(intervalClashEvent => intervalClashEvent._id);

    const allPlayerEventIds = [...fullClashEventIds, ...intervalClashEventIds];
    const allPlayerEventIdsUniq = Lazy(allPlayerEventIds)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const eventsPromise: Promise<SchoolEvent>[] = allPlayerEventIdsUniq.map(eventId => getSchoolEvent(user, eventId));

    Promise.all(eventsPromise).then(events => {
      const eventNames = events
        .map((event, index) => {
          const title = getEventTitle(event, user);
          const clash = getEventClash(event, fullClashEventIds, intervalClashEventIds);
          return `${index + 1}. ${title} ${clash}`;
        })
        .join('\n ');

      this.setState({
        isLoading: false,
        studentEventsModalText: `${firstName} ${lastName} is participating in the following events: \n${eventNames}`,
        isStudentEventsModalOpen: true
      });
    });
  };

  onCloseStudentEventsModalClick = () => {
    this.setState({
      isStudentEventsModalOpen: false
    });
  };

  getFormsFiltered(ages: number[], forms: SchoolForm[], isStrictEqual: boolean): SchoolForm[] {
    const maxAge = Math.max(...ages);
    const minAge = Math.min(...ages);

    switch (true) {
      case isStrictEqual && ages.length === 1:
        return forms.filter(form => form.age === minAge);
      case !isStrictEqual && ages.length === 1:
        return forms.filter(form => form.age <= maxAge);
      case isStrictEqual && ages.length > 1:
        return forms.filter(form => ages.some(age => form.age === age));
      case !isStrictEqual && ages.length > 1:
        return forms.filter(form => ages.some(age => form.age <= age));
      default:
        return [];
    }
  }

  onShowYoungerChange = () => {
    const { user, event } = this.props;
    const { isShowYounger, itemsAccumulatorInitial, itemsAddBuffer, forms, itemsFilters } = this.state;
    const { ages } = event;

    const nextIsShowYounger = !isShowYounger;
    const formsFiltered = this.getFormsFiltered(ages, forms, !nextIsShowYounger);
    const formIds = formsFiltered.map(form => form.id);

    const nextItemsFilters = {
      ...itemsFilters,
      forms: formIds,
      ages: ages
    };

    this.setState({
      isShowYounger: nextIsShowYounger,
      itemsFilters: nextItemsFilters
    });

    const allItems = [...itemsAccumulatorInitial, ...itemsAddBuffer];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();

    const { startTime, endTime, id } = event;
    const { players, selectedTeamId } = this.state as any;

    if (!startTime || !endTime) return;

    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    const where = {
      eventId: id,
      gender: { $in: nextItemsFilters.gender },
      _id: isIndividualTournament(event.tournament)
        ? { $in: this.tournamentStudentIds, $nin: ninIds }
        : { $nin: ninIds },
      formId: { $in: formIds },
      intervalClashStartDate,
      intervalClashEndDate,
      fullClashStartDate: startTime,
      fullClashEndDate: endTime
    };

    const filter = {
      where,
      limit: LIMIT,
      skip: 0,
      order: DEFAULT_TEAM_PLAYERS_ORDER
    };

    const method = getStudentsMethod(user);
    Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]).then(
      ([students, studentsCount]) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          itemCurrentPage: 1,
          itemsSelected: [],
          itemsAccumulatorSelected: []
        });
      }
    );
  };

  onAddClick = () => {
    const { itemsSelected, itemsAddBuffer, itemsAccumulatorInitial, itemsAccumulator, itemCurrentPage } = this.state;
    const { user, event } = this.props;

    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);

    if (typeof maxPlayers === 'number' && maxPlayers > 0) {
      const currentCount = itemsAccumulator.length;
      const tryingToAdd = itemsSelected.length;

      if (currentCount + tryingToAdd > maxPlayers) {
        this.setState({
          slotCandidates: [...itemsSelected]
        });
        return;
      }
    }

    const nextItemsAddBuffer = [...itemsAddBuffer, ...itemsSelected];

    const itemsToAddNormalized: Item[] = itemsSelected.map(item => ({
      id: item.id,
      playerId: '',
      permissionId: item.permissionId,
      form: item.form,
      house: item.house,
      gender: item.gender,
      firstName: item.firstName,
      lastName: item.lastName,
      clashes: item.clashes
    }));

    const existingIds = new Set((itemsAccumulator || []).map(x => String(x.id)));
    const nextItemsAccumulator = [
      ...itemsAccumulator,
      ...itemsToAddNormalized.filter(x => !existingIds.has(String(x.id)))
    ];

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);

    Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]).then(
      ([students, studentsCount]) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          itemsAddBuffer: nextItemsAddBuffer,
          itemsAccumulator: nextItemsAccumulator,
          itemsAccumulatorCount: nextItemsAccumulator.length,
          itemsAccumulatorSelected: [],
          itemsSelected: [],
          isSelectAllItemsAccumulatorChecked: false,
          isSelectAllItemsChecked: false
        });
      }
    );
  };

  onSlotAssignmentConfirm = (assignments: SlotAssignment[]) => {
    const { itemsAccumulator, itemsAddBuffer, itemsRemoveBuffer, slotCandidates } = this.state;
    const assignedIds = new Set(assignments.map(assignment => String(assignment.student.userId)));
    const replacedPlayerIds = new Set(
      assignments.map(assignment => String(assignment.replacingPlayerId || '')).filter(Boolean)
    );
    const replaced = itemsAccumulator.filter(item => replacedPlayerIds.has(String(item.playerId || item.id)));
    const candidates = slotCandidates.filter(item => assignedIds.has(String(item.id)));
    const nextAccumulator = [
      ...itemsAccumulator.filter(item => !replaced.some(value => value.id === item.id)),
      ...candidates.filter(item => !itemsAccumulator.some(value => value.id === item.id))
    ];

    this.setState({
      itemsAccumulator: nextAccumulator,
      itemsAccumulatorCount: nextAccumulator.length,
      itemsAccumulatorSelected: [],
      itemsSelected: [],
      itemsAddBuffer: [
        ...itemsAddBuffer.filter(item => !replaced.some(value => value.id === item.id)),
        ...candidates.filter(item => !itemsAddBuffer.some(value => value.id === item.id))
      ],
      itemsRemoveBuffer: [
        ...itemsRemoveBuffer,
        ...replaced.filter(item => !itemsRemoveBuffer.some(value => value.id === item.id))
      ],
      slotCandidates: []
    });
  };

  onCloseMaxPlayersModal = () => {
    this.setState({ isMaxPlayersModalOpen: false, maxPlayersModalMessage: '' });
  };

  onHelpModalOpen = () => this.setState({ isHelpModalOpen: true });
  onHelpModalClose = () => this.setState({ isHelpModalOpen: false });

  onRequestClose = () => {
    const { itemsAddBuffer, itemsRemoveBuffer } = this.state;
    const hasChanges = itemsAddBuffer.length > 0 || itemsRemoveBuffer.length > 0;
    if (hasChanges) {
      this.setState({ isExitConfirmationModalOpen: true });
    } else {
      this.props.onCancelClick(itemsAddBuffer, itemsRemoveBuffer);
    }
  };

  onExitConfirm = () => {
    const { itemsAddBuffer, itemsRemoveBuffer } = this.state;
    this.setState({ isExitConfirmationModalOpen: false });
    this.props.onCancelClick(itemsAddBuffer, itemsRemoveBuffer);
  };

  onExitCancel = () => {
    this.setState({ isExitConfirmationModalOpen: false });
  };

  onSelectedTeamChange = value => {
    this.setState({
      selectedTeamId: value
    });
  };

  renderStudentEventsModal(): React.ReactNode {
    const { isStudentEventsModalOpen, studentEventsModalText } = this.state;
    return (
      <SimpleModal
        isOpen={isStudentEventsModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsNoticeModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onCloseStudentEventsModalClick}
      >
        <section
          className="tournamentParticipantsNoticeModal isInfo"
          role="dialog"
          aria-label="Participant event clashes"
        >
          <header>
            <div className="tournamentParticipantsNoticeModal__icon">i</div>
            <div>
              <h2>Event clash</h2>
              <p>Review this participant’s other events.</p>
            </div>
            <button type="button" onClick={this.onCloseStudentEventsModalClick} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsNoticeModal__body">{studentEventsModalText}</div>
          <footer>
            <button type="button" className="isPrimary" onClick={this.onCloseStudentEventsModalClick}>
              OK
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderMaxPlayersModal(): React.ReactNode {
    const { isMaxPlayersModalOpen, maxPlayersModalMessage } = this.state;
    return (
      <SimpleModal
        isOpen={isMaxPlayersModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsNoticeModalHost"
        dialogStyle={{ maxWidth: 500 }}
        onBackdropClick={this.onCloseMaxPlayersModal}
      >
        <section
          className="tournamentParticipantsNoticeModal isWarning"
          role="alertdialog"
          aria-label="Player limit reached"
        >
          <header>
            <div className="tournamentParticipantsNoticeModal__icon">!</div>
            <div>
              <h2>Player limit reached</h2>
              <p>The participant limit for this event has been reached.</p>
            </div>
            <button type="button" onClick={this.onCloseMaxPlayersModal} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsNoticeModal__body">{maxPlayersModalMessage}</div>
          <footer>
            <button type="button" className="isPrimary" onClick={this.onCloseMaxPlayersModal}>
              OK
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderHelpModal(): React.ReactNode {
    const { isHelpModalOpen } = this.state;
    const { event } = this.props;
    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);
    const hasLimit = typeof maxPlayers === 'number' && maxPlayers > 0;

    return (
      <HelpModal
        isOpen={isHelpModalOpen}
        title="Edit participants"
        badgeText="Team"
        subtitle="Review eligible students and build your team for this event."
        onClose={this.onHelpModalClose}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Eligible students</div>
              <div className="text-muted">
                The right-hand panel shows students matching the event criteria. Use filters to narrow by name, form,
                house, age or gender.
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Participants</div>
              <div className="text-muted">
                Move students into the participants panel to add them to the team. Changes are staged until you click
                Save.
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Show younger</div>
              <div className="text-muted">
                Toggle "Show younger" to also see students from younger age groups who may still be eligible to
                participate.
              </div>
            </div>
          </div>
          {hasLimit && (
            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Participant limit</div>
                <div className="text-muted">
                  This sport has a maximum of <strong>{maxPlayers}</strong> participants per school. You will not be
                  able to add more than this number.
                </div>
              </div>
            </div>
          )}
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Saving changes</div>
              <div className="text-muted">
                Click Save when ready. Use Cancel to leave without saving — you will be warned if there are unsaved
                changes.
              </div>
            </div>
          </div>
        </div>
      </HelpModal>
    );
  }

  renderExitConfirmationModal(): React.ReactNode {
    const { isExitConfirmationModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isExitConfirmationModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsExitModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onExitCancel}
      >
        <section className="tournamentParticipantsExitModal" role="dialog" aria-label="Discard participant changes">
          <header>
            <div>
              <h2>Leave without saving?</h2>
              <p>Your participant changes have not been saved.</p>
            </div>
            <button type="button" onClick={this.onExitCancel} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsExitModal__body">
            If you leave now, the changes made to this event will be lost.
          </div>
          <footer>
            <button type="button" onClick={this.onExitCancel}>
              Keep editing
            </button>
            <button type="button" className="isDanger" onClick={this.onExitConfirm}>
              Leave without saving
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  render() {
    const {
      items,
      selectedTeamId,
      isLoading,
      itemsSelected,
      itemsAccumulatorSelected,
      itemsAccumulator,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      itemsCount,
      itemsAccumulatorCount,
      itemCurrentPage,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsChecked,
      isSelectAllItemsAccumulatorChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      forms,
      houses,
      isStudentEventsModalOpen,
      isShowYounger
    } = this.state;

    if (isLoading) {
      return (
        <div className="tournamentEventParticipants tournamentEventParticipants--loading">
          <Loader />
        </div>
      );
    }

    const { user, event } = this.props;
    const { activeSchool, activeSchoolId } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;
    const { teamsData } = event;
    const tournament = propz.get(event, ['tournament']);

    const isTeamTournamentCondition = isTeamTournament(tournament);
    const activeSchoolTeams = teamsData.filter(teamData => teamData.schoolId === activeSchoolId);
    const isActiveSchoolHasSeveralTeamsInEvent = activeSchoolTeams.length > 1;

    const eventStartDateAndTimeFormatted = getEventStartDateAndTime(event);
    const eventSportName = propz.get(event, ['sport', 'name'], '');
    const eventGender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender];
    const eventAges = formatTeamAgesCompact(event.ages || [], resolveAgeNamingFromSchool(user.activeSchool));
    const eventTitle = getEventTitle(event, user).replace(getSchoolEventAges(event, user), eventAges);
    const summaryTitle = [eventSportName, eventGender, eventAges].filter(Boolean).join(' · ');

    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);
    const hasLimit = typeof maxPlayers === 'number' && maxPlayers > 0;

    const filterForms = this.getFormsFiltered(event.ages || [], forms, !isShowYounger);
    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(filterForms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(filterForms),
      houses: getSelectOptionForHouses(houses)
    };

    const teamOptions = getSelectOptionForTeams(teamsData);
    const selectedTeamOptionIndex = teamOptions.findIndex(team => team.value === selectedTeamId);

    const classes = isStudentEventsModalOpen ? 'tournamentEventParticipants modal-open' : 'tournamentEventParticipants';
    const eligibleStudentColumns = isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS;

    return (
      <div className={classes}>
        {this.renderStudentEventsModal()}
        {this.renderHelpModal()}
        {this.renderExitConfirmationModal()}
        {this.state.slotCandidates.length > 0 && (
          <SlotAssignmentModal
            isOpen
            event={event}
            newStudents={this.state.slotCandidates.map(item => ({ ...item, userId: item.id })) as any}
            currentParticipants={itemsAccumulator.map(item => ({
              ...item,
              id: item.playerId || item.id,
              userId: item.id
            }))}
            maxPlayers={maxPlayers}
            activeSchoolId={activeSchoolId}
            onConfirm={this.onSlotAssignmentConfirm}
            onClose={() => this.setState({ slotCandidates: [] })}
          />
        )}

        <div className="bSchoolTeamManager__header tournamentEventParticipants__header">
          <div className="bSchoolTeamManager__summaryCard">
            <div className="bSchoolTeamManager__summaryMain">
              <div className="sitPageTitleWithHelp">
                <h2 className="tournamentEventParticipants__title">Edit participants — {eventTitle}</h2>
                <HelpIconButton onClick={this.onHelpModalOpen} label="How tournament team participants work" />
              </div>
              <div className="bSchoolTeamManager__summaryDate">{eventStartDateAndTimeFormatted}</div>
              <div className="bSchoolTeamManager__summaryTitle">{summaryTitle}</div>
              <div className="bSchoolTeamManager__summaryText">
                Update the participants for this event. Changes are staged here and applied when you save.
              </div>
              {hasLimit && (
                <div className="bSchoolTeamManager__summaryText mt-1" style={{ color: '#f0ad4e', fontWeight: 600 }}>
                  Max {maxPlayers} participants per school
                </div>
              )}
              {isTeamTournamentCondition && isActiveSchoolHasSeveralTeamsInEvent && (
                <div className="form-group mb-0 mt-2">
                  <label className="mb-1" style={{ fontWeight: 600 }}>
                    Select team:
                  </label>
                  <Select
                    onChange={this.onSelectedTeamChange}
                    options={teamOptions}
                    activeResultIndex={selectedTeamOptionIndex}
                    customClass={'form-control'}
                  />
                </div>
              )}
              <div className="mt-2">
                <Switch
                  value={isShowYounger}
                  name={'isShowYounger'}
                  onChange={this.onShowYoungerChange}
                  text={'Show younger'}
                />
              </div>
            </div>
            <button
              type="button"
              className="tournamentEventParticipants__close"
              onClick={this.onRequestClose}
              title="Close"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <section className="tournamentEventParticipants__editor">
          <TwoPanelEditor
            isSourceFirst={true}
            itemsModernPagination={true}
            itemsHiddenFilterChipFields={['forms']}
            itemsSearchValue={this.state.itemsSearch}
            onItemsSearchChange={this.onItemsSearchChange}
            isItemsLoading={this.state.isItemsLoading}
            clashEventsById={this.state.clashEventsById}
            itemsAccumulatorCapacity={hasLimit ? maxPlayers : undefined}
            items={items}
            itemsTitle={'Eligible students'}
            itemsColumns={eligibleStudentColumns}
            itemsSelected={itemsSelected}
            onItemClick={this.onItemClick}
            onItemsFilterChange={this.onItemsFilterChange}
            itemsFilters={itemsFilters}
            itemsFiltersOptions={itemsFiltersOptions}
            isShowItemsFilter={isShowItemsFilter}
            isItemsFiltered={isItemsFiltered}
            onItemsFilterClick={this.onItemsFilterClick}
            itemsAccumulator={itemsAccumulator}
            itemsAccumulatorTitle={'Participants'}
            itemsAccumulatorColumns={
              isShowStudentEventClashes ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES : ITEMS_ACCUMULATOR_COLUMNS
            }
            onItemAccumulatorClick={this.onItemAccumulatorClick}
            itemsAccumulatorSelected={itemsAccumulatorSelected}
            onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
            itemsAccumulatorFilters={itemsAccumulatorFilters}
            itemsAccumulatorFiltersOptions={{}}
            isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
            isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
            onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
            onAddClick={this.onAddClick}
            onRemoveClick={this.onRemoveClick}
            itemsCount={itemsCount}
            itemsAccumulatorCount={itemsAccumulatorCount}
            itemAccumulatorCurrentPage={1}
            itemCurrentPage={itemCurrentPage}
            itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
            itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
            itemsOnClearFilterClick={this.itemsOnClearFilterClick}
            itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
            itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
            onSelectAllItemsClick={this.onSelectAllItemsClick}
            onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
            onUnselectAllItemsClick={this.onUnselectAllItemsClick}
            isSelectAllItemsChecked={isSelectAllItemsChecked}
            onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
            onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
            onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
            isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
            sortItemsDirection={sortItemsDirection}
            sortItemsColumnsName={sortItemsColumnsName}
            onSortItemsClick={this.onSortItemsClick}
            sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
            sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
            onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
            itemsAddBuffer={itemsAddBuffer}
            itemsRemoveBuffer={itemsRemoveBuffer}
            onCellItemsClick={this.onClashClick}
            onCellItemsAccumulatorClick={this.onClashClick}
          />
        </section>

        <div className="bSchoolTeamManager__footer tournamentEventParticipants__footer">
          <div className="bSchoolTeamManager__footerRight">
            <Button
              onClick={this.onRequestClose}
              text={'Cancel'}
              customClass={'sit-btn sit-btn--secondary bSchoolTeamManager__btnSecondary'}
            />
            <Button
              text={'Save participants'}
              onClick={this.onClickSave}
              customClass={'sit-btn sit-btn--primary bSchoolTeamManager__btnPrimary'}
            />
          </div>
        </div>
      </div>
    );
  }
}
